import { t } from "@lingui/macro";
import {
  FinishedProjectListTableDataDocument,
  type FinishedProjectListTableDataQuery,
  type FinishedProjectListTableDataQueryVariables,
  FinishedProjectListTableDataSimpleDocument,
  type FinishedProjectListTableDataSimpleQuery,
  type FinishedProjectListTableDataSimpleQueryVariables,
  FinishedProjectListTableSummaryDocument,
  type FinishedProjectListTableSummaryQuery,
  type FinishedProjectListTableSummaryQueryVariables,
  ProjectInvoicedStatusEnum,
  type ProjectListFilterOptionsQuery,
  ProjectListTableDataDocument,
  type ProjectListTableDataQuery,
  type ProjectListTableDataQueryVariables,
  ProjectListTableDataSimpleDocument,
  type ProjectListTableDataSimpleQuery,
  type ProjectListTableDataSimpleQueryVariables,
  ProjectListTableSummaryDocument,
  type ProjectListTableSummaryQuery,
  type ProjectListTableSummaryQueryVariables,
  ProjectOrderByColumnEnum,
  ProjectsSubrowsForPageDocument,
  type ProjectsSubrowsForPageQuery,
  type ProjectsSubrowsForPageQueryVariables,
  ProjectsWhereColumn,
  ProjectTypeEnum,
  SortOrder,
  SqlOperator,
} from "@src/__generated__/graphql";
import type { IOption } from "@src/components/ui-kit";
import { ProjectCategorySelectBottomExtraContent } from "@src/components/ui-kit/ProjectCategorySelect";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import type { AppStore } from "@src/stores/AppStore";
import type { BaseStore } from "@src/stores/BaseStore";
import {
  commonQueryVariables,
  commonSearchParams,
} from "@src/utils/apolloHelpers";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { can } from "@src/utils/components/permissions";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import { action, computed, makeObservable, observable } from "mobx";
import mapToOptions from "../../../../../utils/map-to-options";
import { Project, SubRow } from "../models";
import { ProjectsListBulkActionsStore } from "./bulk-actions";
import { StarProjectModalStore } from "./star-project-modal-store";

export const getBillingCategoryOptions = (): IOption[] => [
  { value: "true", label: t`Billable` },
  { value: "false", label: t`Non-billable` },
];

export enum QuickFilter {
  Pinned = "pinned",
  Active = "active",
  Starred = "starred",
  MyActive = "my_active",
  Finished = "finished",
  Archived = "archived",
}

type TFinishedProjectQuery = NonNullable<
  FinishedProjectListTableDataQuery["projects"]
>["data"][0];
type TFinishedProjectSimple = Pick<
  TFinishedProjectQuery,
  "id" | "code" | "title" | "is_pinned" | "is_starred" | "projectManager"
>;
type TFinishedProjectExtraData = Partial<
  Omit<
    TFinishedProjectQuery,
    "id" | "code" | "title" | "is_pinned" | "is_starred" | "projectManager"
  >
>;
export type TFinishedProject = TFinishedProjectSimple &
  TFinishedProjectExtraData;

export class Store implements BaseStore {
  appStore: AppStore;
  bulkActionsStore: ProjectsListBulkActionsStore;
  starProjectModalStore: StarProjectModalStore;

  // Table data
  @observable.ref projects: Project[] = [];
  @observable.ref finishedProjects: TFinishedProject[] = [];
  @observable.ref
  projectsSummary?: ProjectListTableSummaryQuery["projectsSummary"];
  @observable.ref
  finishedProjectsSummary?: FinishedProjectListTableSummaryQuery["projectsSummary"];

  private subrowsForPageFetcher = new FetchHelper<
    ProjectsSubrowsForPageQuery,
    ProjectsSubrowsForPageQueryVariables
  >(ProjectsSubrowsForPageDocument);

  private projectsSimpleFetcher = new FetchHelper<
    ProjectListTableDataSimpleQuery,
    ProjectListTableDataSimpleQueryVariables
  >(ProjectListTableDataSimpleDocument);

  private finishedProjectsSimpleFetcher = new FetchHelper<
    FinishedProjectListTableDataSimpleQuery,
    FinishedProjectListTableDataSimpleQueryVariables
  >(FinishedProjectListTableDataSimpleDocument);

  private projectsExtraDataFetcher = new FetchHelper<
    ProjectListTableDataQuery,
    ProjectListTableDataQueryVariables
  >(ProjectListTableDataDocument);

  private finishedProjectsExtraDataFetcher = new FetchHelper<
    FinishedProjectListTableDataQuery,
    FinishedProjectListTableDataQueryVariables
  >(FinishedProjectListTableDataDocument);

  private projectsSummaryFetcher = new FetchHelper<
    ProjectListTableSummaryQuery,
    ProjectListTableSummaryQueryVariables
  >(ProjectListTableSummaryDocument);

  private finishedProjectsSummaryFetcher = new FetchHelper<
    FinishedProjectListTableSummaryQuery,
    FinishedProjectListTableSummaryQueryVariables
  >(FinishedProjectListTableSummaryDocument);

  @observable.ref selectedProjectsIds: Project["id"][] = [];

  @observable searchTerm = "";

  pagination = new PaginationState("projects", {
    onChangePagination: () => {
      this.projects = [];
      this.finishedProjects = [];
      this.fetchTableData();
    },
  });

  orderBy = new OrderBy<ProjectOrderByColumnEnum>([
    { column: ProjectOrderByColumnEnum.ProjectCode, order: SortOrder.Asc },
  ]);

  where = new Filters<ProjectsWhereColumn>(
    [
      new Filter({
        column: ProjectsWhereColumn.BrandId,
        operator: SqlOperator.In,
        title: t`Client/Brand`,
        tagTitle: t`Brand`,
        options: [],
        sectioned: true,
        hasSelectAllOption: true,
      }),
      new Filter({
        column: ProjectsWhereColumn.ActiveInTimeframe,
        operator: SqlOperator.Between,
        title: t`Active in time frame`,
        options: [],
        dateRange: true,
      }),
      new Filter({
        column: ProjectsWhereColumn.StartDate,
        operator: SqlOperator.Between,
        title: t`Project start date`,
        options: [],
        dateRange: true,
      }),
      new Filter({
        column: ProjectsWhereColumn.ExpectedEndDate,
        operator: SqlOperator.Between,
        title: t`Project end date`,
        options: [],
        dateRange: true,
      }),
      new Filter({
        column: ProjectsWhereColumn.ProjectManagerId,
        operator: SqlOperator.In,
        title: t`Project Manager`,
        options: [],
        hasSelectAllOption: true,
      }),
      new Filter({
        column: ProjectsWhereColumn.OtherProjectManagerId,
        operator: SqlOperator.In,
        title: t`Other Project Manager`,
        options: [],
        hasSelectAllOption: true,
      }),
      new Filter({
        column: ProjectsWhereColumn.TeamId,
        operator: SqlOperator.In,
        title: t`Team`,
        options: [],
      }),
      new Filter({
        column: ProjectsWhereColumn.InvoicedStatus,
        operator: SqlOperator.In,
        title: t`Status`,
        options: [
          {
            label: t`uninvoiced`,
            value: ProjectInvoicedStatusEnum.Uninvoiced,
          },
          {
            label: t`partially invoiced`,
            value: ProjectInvoicedStatusEnum.PartiallyInvoiced,
          },
          {
            label: t`invoiced`,
            value: ProjectInvoicedStatusEnum.Invoiced,
          },
        ],
      }),
      new Filter({
        column: ProjectsWhereColumn.Billable,
        operator: SqlOperator.Eq,
        title: t`Billing category`,
        options: getBillingCategoryOptions(),
      }),
      new Filter({
        column: ProjectsWhereColumn.Type,
        operator: SqlOperator.Eq,
        title: t`Type`,
        options: [
          {
            value: ProjectTypeEnum.FixedPrice,
            label: t`Fixed-price`,
          },
          {
            value: ProjectTypeEnum.TimeMaterials,
            label: t`Time & Materials`,
          },
        ],
      }),
      new Filter({
        column: ProjectsWhereColumn.QuickFilter,
        operator: SqlOperator.Eq,
        title: t`Projects`,
        hidden: true,
        options: [],
      }),
      new Filter({
        column: ProjectsWhereColumn.CategoryId,
        operator: SqlOperator.In,
        title: t`Project category`,
        options: [],
        bottomExtraContent: <ProjectCategorySelectBottomExtraContent />,
      }),
      new Filter({
        column: ProjectsWhereColumn.IsTemplate,
        operator: SqlOperator.Eq,
        hidden: true,
        options: [],
        value: "false",
      }),
      new Filter({
        column: ProjectsWhereColumn.ProjectStageId,
        operator: SqlOperator.Eq,
        hidden: true,
        options: [],
      }),
    ],
    {
      onChange: () => {
        this.projects = [];
        this.pagination.resetPage();
      },
      tableKey: "projects",
      orderBy: this.orderBy,
    },
  );

  constructor(appStore: AppStore) {
    makeObservable(this);
    this.appStore = appStore;
    this.starProjectModalStore = new StarProjectModalStore(this);
    this.bulkActionsStore = new ProjectsListBulkActionsStore(this);

    if (can("project_read_own") && !can("project_read_all")) {
      this.quickFilter.setValue(QuickFilter.MyActive);
    } else {
      this.quickFilter.setValue(QuickFilter.Active);
    }
  }

  get quickFilter() {
    return this.where.filtersByColumn.get(ProjectsWhereColumn.QuickFilter)!;
  }

  @computed get pipedriveStageFilter() {
    return this.where.filtersByColumn.get(ProjectsWhereColumn.ProjectStageId);
  }

  @computed get searchParams() {
    return commonSearchParams(this);
  }

  @computed get categoryFilter() {
    return this.where.filtersByColumn.get(ProjectsWhereColumn.CategoryId);
  }

  @computed get queryVariables() {
    const vars = commonQueryVariables(this);

    if (can(["project_pin_all", "project_pin_team", "project_pin_own"])) {
      vars.filters.orderBy = [
        { column: ProjectOrderByColumnEnum.Pinned, order: SortOrder.Desc },
        ...vars.filters.orderBy!,
      ];
    }

    return vars;
  }

  @computed get isFinishedOrArchived() {
    const quickFilterValue = this.quickFilter.value[0];
    return (
      quickFilterValue === QuickFilter.Archived ||
      quickFilterValue === QuickFilter.Finished
    );
  }

  @computed
  get rowsLoading(): Set<string> {
    const loadingIds = new Set<string>();

    for (const project of this.projects) {
      if (!project.isLoadingSubrows) continue;
      loadingIds.add(project.id);
    }

    return loadingIds;
  }

  @action.bound handleChangeTab(newFilterValue: QuickFilter) {
    this.pipedriveStageFilter?.clear();
    this.quickFilter.setValue(newFilterValue);
  }

  @action
  setTableData(data: ProjectListTableDataQuery) {
    this.projects = (data.projects?.data ?? []).map((i) => new Project(i));
    this.pagination.setFromPaginatorInfo(data.projects?.paginatorInfo!);
  }

  @action
  setTableOptions(data: ProjectListFilterOptionsQuery) {
    const pmOptions = mapToOptions.projectManagers.toOptions(
      data.projectManagerSimpleMap,
    );

    this.where.filtersByColumn
      .get(ProjectsWhereColumn.ProjectManagerId)
      ?.setOptions(pmOptions);
    this.where.filtersByColumn
      .get(ProjectsWhereColumn.OtherProjectManagerId)
      ?.setOptions(pmOptions);

    this.where.filtersByColumn.get(ProjectsWhereColumn.BrandId)?.setOptions(
      data.clientsSimpleMap.map((i) => ({
        value: i?.id,
        label: i?.name,
        options: i?.brands.map((brand) => {
          return {
            label: brand.name,
            value: brand.id,
          };
        })!,
      })),
    );

    this.where.filtersByColumn.get(ProjectsWhereColumn.TeamId)?.setOptions(
      data.teamSimpleMap.map((i) => ({
        value: i?.id,
        label: i?.name,
      })),
    );
  }

  @action.bound
  setSelectedProjectIds(ids: string[]) {
    this.selectedProjectsIds = ids;
  }

  @action.bound
  handleUncollapseRow(id: string) {
    this.projects.find((i) => i.id === id)?.fetchSubrows();
  }

  @computed
  get isLoadingAllSubrows(): boolean {
    return this.subrowsForPageFetcher.isLoading.value;
  }

  @action.bound
  async handleUncollapseAll() {
    if (this.projects.every(({ canFetchSubrows }) => !canFetchSubrows)) return;
    const [data, error] = await this.subrowsForPageFetcher.fetch(
      this.queryVariables,
    );

    if (error || !data.projects?.data) return;

    for (const project of this.projects) {
      const match = data.projects.data.find((i) => i.id === project.id);
      if (!match) continue;
      project.__subRows = match.ourWork.map((i) => new SubRow(i));
    }
  }

  // Loading states
  @computed
  get isLoading(): boolean {
    return (
      this.projectsSimpleFetcher.isLoading.value ||
      this.finishedProjectsSimpleFetcher.isLoading.value
    );
  }

  @computed
  get isFetchingExtraData(): boolean {
    return this.projectsExtraDataFetcher.isLoading.value;
  }

  @computed
  get isFetchingFinishedExtraData(): boolean {
    return this.finishedProjectsExtraDataFetcher.isLoading.value;
  }

  @computed
  get isLoadingSummary(): boolean {
    return this.projectsSummaryFetcher.isLoading.value;
  }

  @computed
  get isLoadingFinishedSummary(): boolean {
    return this.finishedProjectsSummaryFetcher.isLoading.value;
  }

  @action.bound
  private async fetchProjectsSimple(): Promise<void> {
    if (this.isFinishedOrArchived) return;

    const [data, error] = await this.projectsSimpleFetcher.fetch(
      this.queryVariables,
    );
    if (error || !data.projects) return;

    this.projects = data.projects.data.map((project) => new Project(project));
    this.pagination.setFromPaginatorInfo(data.projects.paginatorInfo);
    this.fetchProjectsExtraData();
  }

  @action.bound
  private async fetchFinishedProjectsSimple(): Promise<void> {
    if (!this.isFinishedOrArchived) return;

    const [data, error] = await this.finishedProjectsSimpleFetcher.fetch(
      this.queryVariables,
    );
    if (error || !data.projects) return;

    this.finishedProjects = data.projects.data;
    this.pagination.setFromPaginatorInfo(data.projects.paginatorInfo);
    this.fetchFinishedProjectsExtraData();
  }

  @action.bound
  private async fetchProjectsExtraData(): Promise<void> {
    if (this.isFinishedOrArchived || this.projects.length === 0) return;

    const [data, error] = await this.projectsExtraDataFetcher.fetch(
      this.queryVariables,
    );
    if (error || !data) return;

    this.setTableData(data);
  }

  @action.bound
  private async fetchFinishedProjectsExtraData(): Promise<void> {
    if (!this.isFinishedOrArchived || this.finishedProjects.length === 0)
      return;

    const [data, error] = await this.finishedProjectsExtraDataFetcher.fetch(
      this.queryVariables,
      null,
      false,
      false,
      "network-only",
    );
    if (error || !data.projects) return;

    this.finishedProjects = data.projects.data;
    this.pagination.setFromPaginatorInfo(data.projects.paginatorInfo);
  }

  @action.bound
  async fetchProjectsSummary(): Promise<void> {
    if (this.isFinishedOrArchived) return;

    const [data, error] = await this.projectsSummaryFetcher.fetch({
      filters: this.queryVariables.filters,
    });
    if (error || !data) return;

    this.projectsSummary = data.projectsSummary;
  }

  @action.bound
  private async fetchFinishedProjectsSummary(): Promise<void> {
    if (!this.isFinishedOrArchived) return;

    const [data, error] = await this.finishedProjectsSummaryFetcher.fetch({
      filters: this.queryVariables.filters,
    });
    if (error || !data) return;

    this.finishedProjectsSummary = data.projectsSummary;
  }

  @action.bound
  fetchTableData(): void {
    if (this.isFinishedOrArchived) {
      this.fetchFinishedProjectsSimple();
      this.fetchFinishedProjectsSummary();
    } else {
      this.fetchProjectsSimple();
      this.fetchProjectsSummary();
    }
  }

  @action.bound
  handleOrderChange(clause: {
    column: ProjectOrderByColumnEnum;
    order: SortOrder | undefined;
  }) {
    if (!clause.order) {
      this.orderBy.setClauses([]);
      this.pagination.resetPage();
      return;
    }
    this.orderBy.setClauses([{ column: clause.column, order: clause.order }]);
    this.pagination.resetPage();
  }
}
